import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from '@mui/material';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { ListDropdown } from 'api/common.types';
import { CustomTextField } from 'components/shared';
import React, { useEffect, useState } from 'react';
import { Module } from 'types/permission';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

interface FiltersDialogProps {
  open: boolean;
  onClose: () => void;
  applyFilters: (filters: {
    name: string;
    group: string;
    module: string;
    status: string;
  }) => Promise<boolean>;
  typesDropdown: ListDropdown[];
  modules: Module[];
  initialFilter: {
    initialName: string;
    initialGroup: string;
    initialModule: string;
    initialStatus: string;
  }
}

const FiltersDialog: React.FC<FiltersDialogProps> = ({
  open,
  onClose,
  applyFilters,
  modules,
  typesDropdown,
  initialFilter
}) => {
  const [name, setName] = useState(initialFilter.initialName || '');
  const [module, setModule] = useState(initialFilter.initialModule || '');
  const [group, setGroup] = useState(initialFilter.initialGroup || '');
  const [status, setStatus] = useState(initialFilter.initialStatus || '');

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open) {
      setName(initialFilter.initialName || '');
      setGroup(initialFilter.initialGroup || '');
      setModule(initialFilter.initialModule || '');
      setStatus(initialFilter.initialStatus || '');
    }
  }, [open, initialFilter.initialName, initialFilter.initialGroup, initialFilter.initialModule, initialFilter.initialStatus]);

  const handleClear = () => {
    setName('');
    setGroup('');
    setModule('');
    setStatus('');
    setErrorMessage('');
  };

  const handleConfirm = async () => {
    const filtersAppliedSuccessfully = await applyFilters({
      name,
      module,
      group,
      status
    });

    if (!filtersAppliedSuccessfully) {
      setErrorMessage(
        'Não foi possível localizar nenhuma Permissão com os dados informados.',
      );
    } else {
      setErrorMessage('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Transition}
      sx={{
        '& .MuiPaper-root': {
          alignItems: 'center',
          height: 'auto',
          width: '100%',
          position: 'fixed',
          margin: 0,
          bottom: 0,

          display: 'flex',

        },
      }}
    >
      <Box sx={{ width: '1200px', marginTop: '60px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#00000026',
              width: '80px',
              height: '5px',
              borderRadius: '100px 0px 0px 0px',

            }}
          />
        </Box>

        <DialogTitle sx={{ padding: 0, marginBottom: '15px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              flexWrap: 'nowrap',

            }}
          >
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontSize: '24px',
                lineHeight: '38.4px',
                fontWeight: 500,
                letterSpacing: '0.15px',
                textAlign: 'left',
              }}
            >
              Filtrar Perfis
            </Typography>
            <Button startIcon={<CloseIcon />} onClick={handleClear}>
              LIMPAR
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ padding: 0 }}>
          {errorMessage && (
            <Box sx={{ margin: '20px 0px 10px 0px' }}>
              <Alert
                severity='error'
                sx={{ position: 'relative !important', height: '48px' }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20.02px',
                    letterSpacing: '0.17px',
                    textAlign: 'left',
                  }}
                >
                  {errorMessage}
                </Typography>
              </Alert>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <CustomTextField
                label='Nome (Opcional)'
                variant='outlined'
                size='small'
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value.toUpperCase())}
                sx={{
                  marginTop: '10px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    legend: {
                      width: '85px',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    left: '5px',
                    top: '5px',
                    fontSize: '14px',
                    fontFamily: 'Roboto',
                    lineHeight: '12px',
                    fontWeight: 400,
                    letterSpacing: '0.15px',
                    color: '#00000099',
                  },
                }}
              />

              <CustomTextField
                label='Grupo (Opcional)'
                variant='outlined'
                size='small'
                select
                fullWidth
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                sx={{
                  marginTop: '10px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    legend: {
                      width: '75px',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    left: '5px',
                    top: '5px',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '12px',
                    letterSpacing: '0.15px',
                    color: '#00000099',
                  },
                }}
              >
                {typesDropdown.map((type) => (
                  <MenuItem
                    key={type.uuid}
                    value={type.uuid}
                    sx={{
                      fontFamily: 'Roboto',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '24px',
                      letterSpacing: '0.15px',
                      textAlign: 'left',
                    }}
                  >
                    {type.name}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>

              <CustomTextField
                label='Módulo (Opcional)'
                variant='outlined'
                size='small'
                select
                fullWidth
                value={module}
                onChange={(e) => setModule(e.target.value)}
                sx={{
                  marginTop: '10px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    legend: {
                      width: '85px',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    left: '5px',
                    top: '5px',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '12px',
                    letterSpacing: '0.15px',
                    color: '#00000099',
                  },
                }}
              >
                {modules.map((module: any) => (
                  <MenuItem
                    key={module.uuid}
                    value={module.uuid}
                    sx={{
                      fontFamily: 'Roboto',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '24px',
                      letterSpacing: '0.15px',
                      textAlign: 'left',
                    }}
                  >
                    {module.description}
                  </MenuItem>
                ))}
              </CustomTextField>


              <CustomTextField
                label='Status (Opcional)'
                variant='outlined'
                size='small'
                select
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value.toUpperCase())}
                sx={{
                  marginTop: '10px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    legend: {
                      width: '75px',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    left: '5px',
                    top: '5px',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '12px',
                    letterSpacing: '0.15px',
                    color: '#00000099',
                  },
                }}
              >
                {['ATIVO', 'INATIVO'].map((status) => (
                  <MenuItem
                    key={status}
                    value={status}
                    sx={{
                      fontFamily: 'Roboto',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '24px',
                      letterSpacing: '0.15px',
                      textAlign: 'left',
                    }}
                  >
                    {status}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Box>

          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'flex-start',
            padding: 0,
            margin: '30px 0px 30px 0px',
          }}
        >
          <Button
            variant='contained'
            size='medium'
            sx={{ backgroundColor: '#0048B6' }}
            onClick={handleConfirm}
          >
            CONFIRMAR
          </Button>
          <Button size='medium' onClick={onClose}>
            VOLTAR
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default FiltersDialog;
